import type { FC, ReactNode } from 'react'

import Link from 'next/link'
import { useEffect, useRef, useState } from 'react'
import { Menu, X } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Container } from '@/components/generals/container'
import { Logo } from '@/components/generals/logo'
import { ButtonLink } from '@/components/generals/button'
import { company, mainNavigation, primaryCta } from '@/constants/site'

const PAGE_REGIONS = ['inhalt', 'seitenende']

export const Header: FC = (): ReactNode => {
    const [scrolled, setScrolled] = useState(false)
    const [menuOpen, setMenuOpen] = useState(false)
    const toggleRef = useRef<HTMLButtonElement>(null)
    const firstLinkRef = useRef<HTMLAnchorElement>(null)

    useEffect(() => {
        const onScroll = (): void => setScrolled(window.scrollY > 12)

        onScroll()
        window.addEventListener('scroll', onScroll, { passive: true })

        return () => window.removeEventListener('scroll', onScroll)
    }, [])

    // The menu only exists below the lg breakpoint, so close it when the viewport grows past it.
    useEffect(() => {
        const query = window.matchMedia('(min-width: 1024px)')
        const onChange = (): void => {
            if (query.matches) setMenuOpen(false)
        }

        query.addEventListener('change', onChange)

        return () => query.removeEventListener('change', onChange)
    }, [])

    // While open: the rest of the page is inert, scrolling is locked and Escape closes the menu.
    useEffect(() => {
        if (!menuOpen) return

        const root = document.documentElement
        const previousOverflow = root.style.overflow
        const regions = PAGE_REGIONS
            .map(id => document.getElementById(id))
            .filter((element): element is HTMLElement => element !== null)

        regions.forEach(element => element.setAttribute('inert', ''))
        root.style.overflow = 'hidden'
        firstLinkRef.current?.focus()

        const onKeyDown = (event: KeyboardEvent): void => {
            if (event.key !== 'Escape') return

            setMenuOpen(false)
            toggleRef.current?.focus()
        }

        document.addEventListener('keydown', onKeyDown)

        return () => {
            regions.forEach(element => element.removeAttribute('inert'))
            root.style.overflow = previousOverflow
            document.removeEventListener('keydown', onKeyDown)
        }
    }, [menuOpen])

    const solid = scrolled || menuOpen

    return (
        <>
            <header
                className={cn(
                    'surface-dark fixed inset-x-0 top-0 z-50 border-b text-kalk-50 transition-[background-color,border-color] duration-300',
                    solid ? 'border-white/10 bg-petrol-950/92 backdrop-blur-md' : 'border-transparent bg-transparent'
                )}
            >
                <Container className='flex h-18 items-center justify-between gap-6'>
                    <Link
                        href='/'
                        aria-label='Gashi Holding, zur Startseite'
                        className='-m-1.5 rounded-md p-1.5'
                        onClick={() => setMenuOpen(false)}
                    >
                        <Logo />
                    </Link>

                    <nav aria-label='Hauptnavigation' className='hidden lg:block'>
                        <ul className='flex items-center gap-1'>
                            {mainNavigation.map(item => (
                                <li key={item.href}>
                                    <Link
                                        href={item.href}
                                        className='inline-flex min-h-10 items-center rounded-full px-4 text-sm text-kalk-50/80 transition-colors duration-200 hover:bg-white/6 hover:text-kalk-50'
                                    >
                                        {item.label}
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    </nav>

                    <div className='flex items-center gap-2'>
                        <ButtonLink href={primaryCta.href} className='hidden sm:inline-flex'>
                            {primaryCta.label}
                        </ButtonLink>

                        <button
                            ref={toggleRef}
                            type='button'
                            aria-expanded={menuOpen}
                            aria-controls='mobile-menu'
                            aria-label={menuOpen ? 'Menü schließen' : 'Menü öffnen'}
                            onClick={() => setMenuOpen(open => !open)}
                            className='inline-flex size-11 items-center justify-center rounded-full border border-white/20 transition-colors duration-200 hover:bg-white/8 lg:hidden'
                        >
                            {menuOpen ? <X className='size-5' aria-hidden='true' /> : <Menu className='size-5' aria-hidden='true' />}
                        </button>
                    </div>
                </Container>
            </header>

            {/* Sibling of the header on purpose: the header's backdrop blur would otherwise trap this fixed panel. */}
            <div
                id='mobile-menu'
                hidden={!menuOpen}
                className='surface-dark fixed inset-x-0 top-18 bottom-0 z-40 overflow-y-auto border-t border-white/10 bg-petrol-950 text-kalk-50 lg:hidden'
            >
                <Container className='flex min-h-full flex-col justify-between gap-12 py-10'>
                    <nav aria-label='Mobile Navigation'>
                        <ul className='flex flex-col'>
                            {mainNavigation.map((item, index) => (
                                <li key={item.href} className='border-b border-white/10'>
                                    <Link
                                        ref={index === 0 ? firstLinkRef : undefined}
                                        href={item.href}
                                        onClick={() => setMenuOpen(false)}
                                        className='flex items-center justify-between py-4 font-heading stretch-wide text-2xl font-semibold tracking-[-0.02em]'
                                    >
                                        {item.label}

                                        <span aria-hidden='true' className='font-mono text-xs font-normal text-petrol-200'>
                                            {String(index + 1).padStart(2, '0')}
                                        </span>
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    </nav>

                    <div className='flex flex-col gap-6'>
                        <ButtonLink href={primaryCta.href} size='lg' onClick={() => setMenuOpen(false)}>
                            {primaryCta.label}
                        </ButtonLink>

                        <p className='text-sm leading-relaxed text-petrol-200'>
                            {company.legalName}
                            <br />
                            {company.street}, {company.postalCode} {company.city}
                        </p>
                    </div>
                </Container>
            </div>
        </>
    )
}
